<template>
    <section
        v-if="persistentAnnouncements.length && mode === 'banner'"
        class="persistent-announcements"
        aria-label="Persistent team information"
    >
        <div class="persistent-announcements__inner">
            <article
                v-for="announcement in persistentAnnouncements"
                :key="announcement.id"
                class="persistent-announcement"
            >
                <i class="fas fa-bullhorn persistent-announcement__icon" aria-hidden="true"></i>
                <div class="persistent-announcement__copy">
                    <h2>{{ announcement.title }}</h2>
                    <p>{{ announcement.body }}</p>
                </div>
            </article>
        </div>
    </section>

    <div
        v-else-if="persistentAnnouncements.length"
        class="persistent-announcements-compact"
    >
        <CellPopover
            title="Team information"
            :label="compactLabel"
            trigger-tag="button"
            trigger-class="persistent-announcements-compact__trigger"
            :min-width="320"
            :max-width="420"
            align="end"
        >
            <template #trigger>
                <i class="fas fa-bullhorn" aria-hidden="true"></i>
                <span class="persistent-announcements-compact__count" aria-hidden="true">
                    {{ persistentAnnouncements.length }}
                </span>
            </template>

            <div class="persistent-announcements-panel">
                <article
                    v-for="announcement in persistentAnnouncements"
                    :key="announcement.id"
                    class="persistent-announcements-panel__item"
                >
                    <h2>{{ announcement.title }}</h2>
                    <p>{{ announcement.body }}</p>
                </article>
            </div>
        </CellPopover>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { useAnnouncementFeed } from "@/composables/queries/useAnnouncements";
import CellPopover from "@/components/common/CellPopover.vue";

defineProps({
    mode: {
        type: String,
        default: "banner",
        validator: (value) => ["banner", "compact"].includes(value)
    }
});

const store = useStore();
const canReceiveAnnouncements = computed(
    () => !["auditor", "supplier"].includes(store.getters.userRole?.())
);
const { data } = useAnnouncementFeed(canReceiveAnnouncements);
const persistentAnnouncements = computed(() =>
    (data.value || []).filter((announcement) => announcement.kind === "persistent")
);
const compactLabel = computed(() => {
    const count = persistentAnnouncements.value.length;
    return `${count} persistent team ${count === 1 ? "announcement" : "announcements"}`;
});
</script>

<style scoped>
.persistent-announcements {
    padding: 0.75rem max(var(--forkto-page-gutter-desktop, 3rem), var(--forkto-safe-area-right))
        0 max(var(--forkto-page-gutter-desktop, 3rem), var(--forkto-safe-area-left));
}

.persistent-announcements__inner {
    display: grid;
    gap: 0.5rem;
    max-width: 1280px;
    margin: 0 auto;
}

.persistent-announcement {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.75rem;
    align-items: start;
    padding: 0.7rem 0.875rem;
    color: var(--forkto-ink-2);
    background: var(--forkto-surface);
    border: 1px solid var(--forkto-line);
    border-left: 3px solid var(--forkto-ink-3);
    border-radius: var(--forkto-radius-control);
}

.persistent-announcement__icon {
    margin-top: 0.2rem;
    color: var(--forkto-ink-3);
}

.persistent-announcement__copy {
    min-width: 0;
}

.persistent-announcement h2 {
    margin: 0 0 0.125rem;
    color: var(--forkto-ink-1);
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.persistent-announcement p {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

.persistent-announcements-compact {
    display: inline-flex;
    flex: 0 0 auto;
}

.persistent-announcements-compact :deep(.persistent-announcements-compact__trigger) {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    width: auto;
    min-width: 2rem;
    min-height: 2rem;
    padding: 0.25rem 0.375rem;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    background: transparent;
    border: 0;
    border-radius: var(--forkto-radius-control);
    cursor: pointer;
}

.persistent-announcements-compact :deep(.persistent-announcements-compact__trigger:hover) {
    color: var(--forkto-ink-1);
    background: var(--forkto-sunk);
}

.persistent-announcements-compact :deep(.persistent-announcements-compact__trigger:focus-visible) {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 1px;
}

.persistent-announcements-compact__count {
    min-width: 0.55rem;
    color: var(--forkto-ink-1);
    font-size: 0.6875rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    text-align: center;
}

.persistent-announcements-panel {
    display: grid;
}

.persistent-announcements-panel__item {
    padding: 0.25rem 0.25rem 0.75rem;
}

.persistent-announcements-panel__item + .persistent-announcements-panel__item {
    padding-top: 0.75rem;
    border-top: 1px solid var(--forkto-line);
}

.persistent-announcements-panel__item:last-child {
    padding-bottom: 0.25rem;
}

.persistent-announcements-panel h2 {
    margin: 0 0 0.25rem;
    color: var(--forkto-ink-1);
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.persistent-announcements-panel p {
    margin: 0;
    color: var(--forkto-ink-2);
    font-size: 0.8125rem;
    line-height: 1.5;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}

@media (max-width: 1279.98px) {
    .persistent-announcements {
        padding-top: 0.5rem;
    }

    .persistent-announcements-compact {
        display: flex;
        justify-content: flex-end;
        padding: 0.5rem 0.75rem 0;
    }

    .persistent-announcements-compact :deep(.persistent-announcements-compact__trigger) {
        min-width: 44px;
        min-height: 44px;
    }
}

@media (max-width: 991.98px) {
    .persistent-announcements {
        padding-right: max(0.75rem, var(--forkto-safe-area-right));
        padding-left: max(0.75rem, var(--forkto-safe-area-left));
    }
}
</style>
